import { Button, Pagination, Space, Table, Tooltip, notification } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { mapStarError } from 'pages/star-shared/star-error';
import { PenaltyRow } from 'pages/star-shared/types';
import { formatDateTime, notifyBulk, personName, runBulk } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import { Ellipsis, NoWrap, UserCell } from 'pages/star-shared/components/status-tags';
import NoteActionModal, { NoteActionConfig } from 'pages/star-shared/components/note-action-modal';
import { colors } from 'theme/tokens';

const LIMIT = 20;
type WaiveAction = 'approve-waive' | 'reject-waive';

const ACTION_META: Record<WaiveAction, { verb: string; title: string; confirm: string }> = {
  'approve-waive': {
    verb: 'Đã duyệt xoá phạt',
    title: 'Duyệt xoá phạt',
    confirm: 'Duyệt xoá phạt',
  },
  'reject-waive': {
    verb: 'Đã từ chối',
    title: 'Từ chối & hoàn Sao',
    confirm: 'Từ chối & hoàn Sao',
  },
};

// Hàng chờ xoá phạt (loại REVIEW): Sao đã bị trừ lúc xin xoá; duyệt = WAIVED, từ chối = hoàn Sao, phạt về OPEN.
const WaivesTab = () => {
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [action, setAction] = useState<NoteActionConfig | null>(null);
  const { items, total, loading } = usePagedList<PenaltyRow>(
    API.starsPenalties,
    { status: 'WAIVE_PENDING', page, limit: LIMIT },
    { refreshKey }
  );

  const refresh = () => {
    setSelectedRowKeys([]);
    setRefreshKey((k) => k + 1);
  };

  const call = (id: number, act: WaiveAction, note?: string) =>
    AxiosUtils.patch(`${API.starsPenalties}/${id}/${act}`, note ? { note } : {});

  const openSingle = (p: PenaltyRow, act: WaiveAction) => {
    const meta = ACTION_META[act];
    setAction({
      title: meta.title,
      description: (
        <span>
          #{p.id} · {personName(p.user)} · {p.typeName} · đã trừ <StarAmount value={p.starCost} />
          {act === 'reject-waive' && (
            <div style={{ color: colors.textSecondary, marginTop: 4 }}>
              Sao sẽ được hoàn lại, bản ghi phạt quay về Đang mở.
            </div>
          )}
        </span>
      ),
      noteRequired: act === 'reject-waive',
      noteLabel: act === 'reject-waive' ? 'Lý do từ chối' : 'Ghi chú (không bắt buộc)',
      confirmText: meta.confirm,
      danger: act === 'reject-waive',
      onSubmit: async (note) => {
        try {
          await call(p.id, act, note);
          notification.success({ message: `${meta.verb} #${p.id}.` });
          refresh();
        } catch (err) {
          notification.error({ message: mapStarError(err) });
          throw err;
        }
      },
    });
  };

  const openBulk = (act: WaiveAction) => {
    const ids = selectedRowKeys.map(Number);
    if (!ids.length) return;
    const meta = ACTION_META[act];
    setAction({
      title: `${meta.title} — ${ids.length} bản ghi đã chọn`,
      description: 'Ghi chú (nếu có) sẽ áp dụng cho tất cả bản ghi đã chọn.',
      noteRequired: act === 'reject-waive',
      noteLabel: act === 'reject-waive' ? 'Lý do từ chối' : 'Ghi chú (không bắt buộc)',
      confirmText: `${meta.confirm} (${ids.length})`,
      danger: act === 'reject-waive',
      onSubmit: async (note) => {
        const result = await runBulk(ids, (id) => call(id, act, note));
        notifyBulk(result, meta.verb);
        refresh();
      },
    });
  };

  const columns: ColumnsType<PenaltyRow> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Người xin xoá', key: 'user', render: (_, p) => <UserCell user={p.user} /> },
    {
      title: 'Loại phạt',
      dataIndex: 'typeName',
      key: 'typeName',
      render: (v) => <strong>{v}</strong>,
    },
    {
      title: 'Sao đã trừ',
      key: 'starCost',
      width: 110,
      align: 'right',
      render: (_, p) => <StarAmount value={p.starCost} />,
    },
    {
      title: 'Ghi chú phạt',
      key: 'note',
      render: (_, p) => <Ellipsis value={p.note} width={220} />,
    },
    {
      title: 'Ghi phạt lúc',
      key: 'recordedAt',
      width: 180,
      render: (_, p) => (
        <div>
          <NoWrap>{formatDateTime(p.recordedAt)}</NoWrap>
          {p.recordedBy && (
            <div style={{ color: colors.textSecondary, fontSize: 12 }}>
              bởi {personName(p.recordedBy)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Xin xoá lúc',
      key: 'waiveRequestedAt',
      width: 160,
      render: (_, p) => <NoWrap>{formatDateTime(p.waiveRequestedAt)}</NoWrap>,
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 100,
      render: (_, p) => (
        <Space size="small">
          <Tooltip title="Duyệt xoá phạt">
            <Button
              type="text"
              icon={<CheckOutlined />}
              onClick={() => openSingle(p, 'approve-waive')}
            />
          </Tooltip>
          <Tooltip title="Từ chối & hoàn Sao">
            <Button
              danger
              type="text"
              icon={<CloseOutlined />}
              onClick={() => openSingle(p, 'reject-waive')}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<CheckOutlined />}
          disabled={!selectedRowKeys.length}
          onClick={() => openBulk('approve-waive')}
        >
          Duyệt xoá đã chọn
        </Button>
        <Button
          danger
          icon={<CloseOutlined />}
          disabled={!selectedRowKeys.length}
          onClick={() => openBulk('reject-waive')}
        >
          Từ chối đã chọn
        </Button>
        {selectedRowKeys.length > 0 && <span>Đã chọn {selectedRowKeys.length}</span>}
      </div>
      <div className="custom-table">
        <Table
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={items}
          loading={loading}
          locale={{ emptyText: 'Không có yêu cầu xoá phạt chờ duyệt.' }}
          scroll={{ x: 1100 }}
        />
        {total > LIMIT && (
          <Pagination
            showSizeChanger={false}
            current={page}
            onChange={setPage}
            showLessItems
            total={total}
            pageSize={LIMIT}
          />
        )}
      </div>
      <NoteActionModal config={action} onClose={() => setAction(null)} />
    </div>
  );
};

export default WaivesTab;
